import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IMultiSelectOption } from 'ngx-bootstrap-multiselect';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { DateTime } from 'luxon';

import { ProgramService } from '@program/shared/program.service';
import { Program } from '@program/shared/program.model';
import { ModalDialogComponent } from '@shared/components/modal-dialog/modal-dialog.component';
import { DocumentDataService } from '@shared/components/files/shared/document-data.service';
import { DownloadedExportsService } from '@shared/components/files/shared/downloaded-exports.service';
import { DocumentExportJob } from '@shared/components/files/shared/models/document-export-job.model';
import { DocumentExportJobMode } from '@shared/components/files/shared/models/document-export-job-mode.enum';
import { DocumentExportJobStatus } from '@shared/components/files/shared/models/document-export-job-status.enum';
import { DocumentExportSubmitResponse } from '@shared/components/files/shared/models/document-export-submit-response.model';
import { SharedExportAssociationType } from '@shared/components/files/shared/models/shared-export-association-type.enum';
import { SharedExportEstimateResponse } from '@shared/components/files/shared/models/shared-export-estimate-response.model';
import { SharedExportFormUploadFilter } from '@shared/components/files/shared/models/shared-export-form-upload-filter.type';
import { SharedExportRequest } from '@shared/components/files/shared/models/shared-export-request.model';
import { EventService } from '@shared/event.service';
import { FilesActionRuleService } from '@shared/components/files/shared/files-action-rule.service';
import { TranslateService } from '@ngx-translate/core';

type UploadDateMode = 'ANY' | 'ON_OR_AFTER' | 'ON_OR_BEFORE' | 'BETWEEN';

@Component({
  selector: 'oes-shared-export-modal',
  templateUrl: './shared-export-modal.component.html',
  styleUrls: ['./shared-export-modal.component.scss'],
})
export class SharedExportModalComponent implements OnInit, OnDestroy {
  @Input() organizationId: string;
  @Output() closed = new EventEmitter<void>();
  @Output() exportSubmitted = new EventEmitter<void>();

  @ViewChild(ModalDialogComponent, { static: false })
  private modalDialog: ModalDialogComponent;

  associationOptions: IMultiSelectOption[] = [];
  estimateResponse: SharedExportEstimateResponse;
  form: FormGroup;
  loadingPrograms = false;
  estimating = false;
  submitting = false;
  polling = false;
  downloading = false;
  pollingStatus: DocumentExportJobStatus | null = null;
  inlineError: string | null = null;
  currentJobId: string | null = null;
  programOptions: IMultiSelectOption[] = [];

  private readonly destroyed$ = new Subject<void>();

  readonly formUploadOptions: Array<{ value: SharedExportFormUploadFilter; labelKey: string }> = [
    { value: 'ALL', labelKey: 'files.shared-export.form-upload-options.all' },
    { value: 'FORM_ONLY', labelKey: 'files.shared-export.form-upload-options.form-only' },
    { value: 'NON_FORM_ONLY', labelKey: 'files.shared-export.form-upload-options.non-form-only' },
  ];

  readonly uploadDateModeOptions: Array<{ value: UploadDateMode; labelKey: string }> = [
    { value: 'ANY', labelKey: 'files.shared-export.date-mode-options.any' },
    { value: 'ON_OR_AFTER', labelKey: 'files.shared-export.date-mode-options.on-or-after' },
    { value: 'ON_OR_BEFORE', labelKey: 'files.shared-export.date-mode-options.on-or-before' },
    { value: 'BETWEEN', labelKey: 'files.shared-export.date-mode-options.between' },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private documentDataService: DocumentDataService,
    private downloadedExportsService: DownloadedExportsService,
    private eventService: EventService,
    private filesActionRuleService: FilesActionRuleService,
    private programService: ProgramService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.associationOptions = [
      {
        id: SharedExportAssociationType.PROJECT,
        name: this.translateService.instant('files.shared-export.association-options.project'),
      },
      {
        id: SharedExportAssociationType.MILESTONE,
        name: this.translateService.instant('files.shared-export.association-options.milestone'),
      },
      {
        id: SharedExportAssociationType.PREQUALIFICATION,
        name: this.translateService.instant('files.shared-export.association-options.program-application'),
      },
      {
        id: SharedExportAssociationType.PROJECT_GROUP,
        name: this.translateService.instant('files.shared-export.association-options.portfolio'),
      },
    ];

    this.form = this.formBuilder.group({
      programId: ['', Validators.required],
      associationType: ['', Validators.required],
      filenameContains: [''],
      extensionsInput: [''],
      uploadDateMode: ['ANY'],
      uploadDateFrom: [null],
      uploadDateTo: [null],
      formUploadFilter: ['ALL'],
    });

    this.form.valueChanges.subscribe(() => {
      this.estimateResponse = null;
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  show(): void {
    this.resetState();
    this.loadPrograms();
    this.modalDialog.show();
  }

  hide(): void {
    // Cancel any in-flight polling when the user closes the modal. The backend job still
    // runs to completion; the user can retrieve the result from the Current Exports panel
    // or (for async jobs) the email link.
    this.stopPolling();
    this.modalDialog.hide();
  }

  onModalClosed(): void {
    this.stopPolling();
    this.closed.emit();
  }

  estimate(): void {
    if (!this.validateFormForEstimate()) {
      return;
    }

    this.estimating = true;
    this.documentDataService
      .estimateSharedExport(this.organizationId, this.buildRequest())
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.estimateResponse = response;
          this.estimating = false;
        },
        error: (error) => {
          this.estimating = false;
          this.eventService.error(
            error?.error?.error
              || this.translateService.instant('files.shared-export.estimate-failed')
          );
        },
      });
  }

  download(): void {
    if (!this.canDownload()) {
      return;
    }

    this.inlineError = null;
    this.submitting = true;
    this.documentDataService
      .submitSharedExport(this.organizationId, this.buildRequest())
      .pipe(take(1))
      .subscribe({
        next: (response) => this.onSubmitSuccess(response),
        error: (error) => this.onSubmitError(error),
      });
  }

  private onSubmitSuccess(response: DocumentExportSubmitResponse): void {
    this.submitting = false;
    this.currentJobId = response.jobId;

    // Notify the parent that a new job was accepted. The Current Exports panel listens for
    // this so it can refresh its list and pick up the new QUEUED row.
    this.exportSubmitted.emit();

    if (response.mode === DocumentExportJobMode.ASYNC) {
      // Large export — tell the user we'll email them and close the modal.
      this.eventService.success(
        this.translateService.instant('files.shared-export.email-toast')
      );
      this.hide();
      return;
    }

    // Small export — poll until terminal, then initiate the browser download.
    this.polling = true;
    this.pollingStatus = response.status;
    this.documentDataService
      .pollExportJob(this.organizationId, response.jobId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (job) => this.onPollTick(job),
        error: (error) => this.onPollError(error),
      });
  }

  private onPollTick(job: DocumentExportJob): void {
    this.pollingStatus = job.status;

    if (job.status === DocumentExportJobStatus.COMPLETED) {
      this.triggerDownload(job.jobId);
      return;
    }

    if (
      job.status === DocumentExportJobStatus.FAILED ||
      job.status === DocumentExportJobStatus.EXPIRED
    ) {
      this.polling = false;
      this.pollingStatus = null;
      this.inlineError =
        job.errorMessage ||
        this.translateService.instant('files.shared-export.job-failed');
    }
  }

  private triggerDownload(jobId: string): void {
    this.polling = false;
    this.downloading = true;
    this.documentDataService
      .downloadExportJob(this.organizationId, jobId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.downloading = false;
          this.downloadedExportsService.markDownloaded(jobId);
          this.eventService.success(
            this.translateService.instant('files.shared-export.inline-success-toast')
          );
          this.hide();
        },
        error: (error) => {
          this.downloading = false;
          this.inlineError =
            error?.message ||
            this.translateService.instant('files.shared-export.download-failed');
        },
      });
  }

  private onSubmitError(error: unknown): void {
    this.submitting = false;
    const status = (error as { status?: number })?.status;
    const backendMessage = (error as { error?: { error?: string } })?.error?.error;

    if (status === 429) {
      this.inlineError =
        backendMessage ||
        this.translateService.instant('files.shared-export.queue-full');
      return;
    }

    this.inlineError =
      backendMessage ||
      this.translateService.instant('files.shared-export.download-failed');
  }

  private onPollError(error: unknown): void {
    this.polling = false;
    this.pollingStatus = null;
    const backendMessage = (error as { error?: { error?: string } })?.error?.error;
    this.inlineError =
      backendMessage ||
      this.translateService.instant('files.shared-export.download-failed');
  }

  private stopPolling(): void {
    this.polling = false;
    this.pollingStatus = null;
    this.currentJobId = null;
  }

  get hasMatchingFiles(): boolean {
    return !!this.estimateResponse &&
      this.estimateResponse.fileCount > 0 &&
      this.estimateResponse.totalBytes > 0;
  }

  get estimateAllowed(): boolean {
    return this.hasMatchingFiles &&
      this.estimateResponse.totalBytes <= this.filesActionRuleService.maxTotalBytes;
  }

  canDownload(): boolean {
    return (
      this.estimateAllowed &&
      !this.estimating &&
      !this.submitting &&
      !this.polling &&
      !this.downloading
    );
  }

  canEstimate(): boolean {
    return (
      this.form.get('programId')?.valid &&
      this.form.get('associationType')?.valid &&
      !this.estimating &&
      !this.submitting &&
      !this.polling &&
      !this.downloading
    );
  }

  isDateControlVisible(controlName: 'uploadDateFrom' | 'uploadDateTo'): boolean {
    const mode = this.form.get('uploadDateMode')?.value as UploadDateMode;
    return controlName === 'uploadDateFrom'
      ? mode === 'ON_OR_AFTER' || mode === 'BETWEEN'
      : mode === 'ON_OR_BEFORE' || mode === 'BETWEEN';
  }

  formatBytes(totalBytes?: number): string {
    if (!totalBytes) {
      return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = totalBytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    const precision = unitIndex === 0 ? 0 : 1;
    return `${size.toFixed(precision)} ${units[unitIndex]}`;
  }

  private buildRequest(): SharedExportRequest {
    const uploadDateMode = this.form.get('uploadDateMode')?.value as UploadDateMode;
    const uploadDateFrom = this.form.get('uploadDateFrom')?.value as Date;
    const uploadDateTo = this.form.get('uploadDateTo')?.value as Date;
    const formUploadFilter = this.form.get('formUploadFilter')?.value as SharedExportFormUploadFilter;
    const associationType = this.form.get('associationType')?.value as SharedExportAssociationType;

    const request: SharedExportRequest = {
      programId: this.form.get('programId')?.value,
      associationTypes: associationType ? [associationType] : [],
    };

    const filenameContains = (this.form.get('filenameContains')?.value || '').trim();
    if (filenameContains) {
      request.filenameContains = filenameContains;
    }

    const extensions = this.parseExtensions(this.form.get('extensionsInput')?.value || '');
    if (extensions.length) {
      request.extensions = extensions;
    }

    if (uploadDateMode === 'ON_OR_AFTER' || uploadDateMode === 'BETWEEN') {
      request.uploadDateFrom = DateTime.fromJSDate(uploadDateFrom)
        .startOf('day')
        .toUTC()
        .toISO();
    }
    if (uploadDateMode === 'ON_OR_BEFORE' || uploadDateMode === 'BETWEEN') {
      request.uploadDateTo = DateTime.fromJSDate(uploadDateTo)
        .endOf('day')
        .toUTC()
        .toISO();
    }

    if (formUploadFilter === 'FORM_ONLY') {
      request.formUpload = true;
    } else if (formUploadFilter === 'NON_FORM_ONLY') {
      request.formUpload = false;
    }

    return request;
  }

  private loadPrograms(): void {
    if (!this.organizationId) {
      return;
    }

    this.loadingPrograms = true;
    this.programService.getPrograms()
      .pipe(take(1))
      .subscribe({
        next: (programs: Program[]) => {
          this.programOptions = programs
            .filter((program) => program.sponsor?.id === this.organizationId)
            .map((program) => ({
              id: program.id,
              name: program.name,
            }));
          this.loadingPrograms = false;
        },
        error: () => {
          this.loadingPrograms = false;
          this.eventService.error(
            this.translateService.instant('files.shared-export.program-load-failed')
          );
        },
      });
  }

  private parseExtensions(rawValue: string): string[] {
    const values = rawValue
      .split(/[\s,]+/)
      .map((value) => value.trim().toLowerCase())
      .filter((value) => !!value)
      .map((value) => value.startsWith('.') ? value : `.${value}`);

    return Array.from(new Set(values));
  }

  private resetState(): void {
    this.form.reset({
      programId: '',
      associationType: '',
      filenameContains: '',
      extensionsInput: '',
      uploadDateMode: 'ANY',
      uploadDateFrom: null,
      uploadDateTo: null,
      formUploadFilter: 'ALL',
    });
    this.estimateResponse = null;
    this.estimating = false;
    this.submitting = false;
    this.polling = false;
    this.downloading = false;
    this.pollingStatus = null;
    this.inlineError = null;
    this.currentJobId = null;
  }

  private validateFormForEstimate(): boolean {
    const uploadDateMode = this.form.get('uploadDateMode')?.value as UploadDateMode;
    const uploadDateFrom = this.form.get('uploadDateFrom')?.value as Date;
    const uploadDateTo = this.form.get('uploadDateTo')?.value as Date;

    if (uploadDateMode === 'ON_OR_AFTER' && !uploadDateFrom) {
      this.form.get('uploadDateFrom')?.markAsTouched();
      return false;
    }
    if (uploadDateMode === 'ON_OR_BEFORE' && !uploadDateTo) {
      this.form.get('uploadDateTo')?.markAsTouched();
      return false;
    }
    if (uploadDateMode === 'BETWEEN' && (!uploadDateFrom || !uploadDateTo)) {
      this.form.get('uploadDateFrom')?.markAsTouched();
      this.form.get('uploadDateTo')?.markAsTouched();
      return false;
    }

    this.form.markAllAsTouched();
    return this.form.valid;
  }
}
